// Weekly goals (2026-10-27; plans_parked "Brisk minutes", goals): brisk minutes against the WHO's
// 150 a week, and days with 5,000 steps (Fit's default daily goal). Fixed for now. Walks only: no
// all-day steps as Fit has. Weeks run Monday to Sunday by UK date.
import { addDays, dateKey, utcDate } from './dates.js'
import { ukDateKey } from './format.js'

export const WEEK_BRISK_GOAL = 150
export const DAY_STEPS_GOAL = 5000

// "2026-10-26" (a Monday) for any UK day key in that week.
export function weekOf(key) {
  const d = utcDate(key)
  return dateKey(addDays(d, -((d.getUTCDay() + 6) % 7)))
}

// A real walk's figure: its counted recording's, else that of the recording of the same walk with
// the most steps that has one. Brisk minutes exist only on Waystamp recordings with step samples
// (since 26 Oct 2026), and most counted recordings are MapMyWalk's, so this usually reaches across.
function figureOf(counted, recordings, field) {
  const has = (w) => w[field] != null && (field !== 'steps' || w[field] > 0)
  if (has(counted)) return counted[field]
  const best = recordings.filter(has).sort((a, b) => (b.steps ?? 0) - (a.steps ?? 0))[0]
  return best ? best[field] : null
}

// UK day key → {walks, brisk, briskMissing, steps}: each real walk once (walk_groups' counted
// recording). brisk is null when no walk that day has a figure; briskMissing counts walks without.
export function dayFigures(walks) {
  const groups = new Map()
  for (const w of walks) {
    if (!w.group_id) continue
    if (!groups.has(w.group_id)) groups.set(w.group_id, [])
    groups.get(w.group_id).push(w)
  }
  const days = new Map()
  for (const w of walks) {
    if (!w.is_counted) continue
    const recordings = w.group_id ? groups.get(w.group_id) : [w]
    const key = ukDateKey(w.start_time)
    const day = days.get(key) ?? { walks: 0, brisk: null, briskMissing: 0, steps: 0 }
    day.walks++
    const brisk = figureOf(w, recordings, 'brisk_minutes')
    if (brisk == null) day.briskMissing++
    else day.brisk = (day.brisk ?? 0) + brisk
    day.steps += figureOf(w, recordings, 'steps') ?? 0
    days.set(key, day)
  }
  return days
}

// The week starting on Monday `monday`: its seven days and totals.
export function weekSummary(days, monday) {
  const list = Array.from({ length: 7 }, (_, i) => {
    const key = dateKey(addDays(utcDate(monday), i))
    return { key, ...(days.get(key) ?? { walks: 0, brisk: null, briskMissing: 0, steps: 0 }) }
  })
  return {
    monday,
    days: list,
    brisk: list.reduce((s, d) => s + (d.brisk ?? 0), 0),
    briskMissing: list.reduce((s, d) => s + d.briskMissing, 0),
    stepDays: list.filter((d) => d.steps >= DAY_STEPS_GOAL).length,
    walks: list.reduce((s, d) => s + d.walks, 0),
  }
}

// The first week with any brisk minutes, or null.
export function firstBriskWeek(days) {
  const keys = [...days.entries()].filter(([, d]) => d.brisk != null).map(([k]) => k).sort()
  return keys.length ? weekOf(keys[0]) : null
}
